import { FontIcon } from "@fluentui/react";
import { AddIcon, Button, Dialog, Flex, MenuButton, MoreIcon, ProviderConsumer } from "@fluentui/react-northstar";
import { themeNames } from "@fluentui/react-teams";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { transformModalContent } from "../../../automation/AutomationTable";
import useCurrentPage from "../../../kb/useCurrentPage";
import { getCachedFeature } from "../../../shared/cache/FeatureCache";
import { EmptyData } from "../../../shared/components/EmptyData";
import { LoadingScreen } from "../../../shared/components/LoadingScreen";
import { TableWrapper } from "../../../shared/components/table/TableWrapper";
import { Toolbar } from '../../../shared/components/Toolbar';
import { ICustomProperty } from "../../../shared/interfaces/customproperties.interface";
import { platformService } from "../../../shared/services/platform.service";
import { BUTTONS, DATASOURCE_HEADERS, DELETE, LICENSE_FEATURE, LOADING, TEMPLATE_DROPDOWN_RETURN_LABEL } from "../../../shared/utils/constants";
import { toTitleCase } from '../../../shared/utils/helper';
import { toolbarButtonColor } from "../../../tikit/ticketHelper";
import EmptySVG from './../../../../svg/empty.svg';
import Styles from './datasource.module.css';

export interface IDataSource {
    Id: number,
    Name: string,
    Description: string,
    EntityType: string,
    SelectedValues?: string[],
    SelectedItems?: string,
    CustomPropertyId?: number,
    CustomProperty?: ICustomProperty
}

interface Props{
  onRowClickHandler?: (id: number) => void;
  onAddClickHandler?: () => void;
}

export const CustomElementDataSourceTable = ({onRowClickHandler, onAddClickHandler}: Props) => {

    const { t } = useTranslation();
    const api = new platformService();
    const [rows, setRows] = useState<RowsData[]>([]);
    const {currentPage, setCurrentPage} = useCurrentPage();
    const [isLoading, setIsLoading] = useState(true);
    const [recordToDelete, setRecordToDelete] = useState<Partial<IDataSource>>({ Id: 0, Name: ''});
    const [showDialog, setShowDialog] = useState(false);

    const headers: string[] = DATASOURCE_HEADERS;

    const deleteDatasource = async() => {
      setShowDialog(false);
      setIsLoading(true);
      let status = 0;
      ({ status } = await api.deleteDataSource(recordToDelete['Id']) );
      if (status === 204) fetchData();
      setIsLoading(false);
    };
    
    const fetchData = useCallback(async () => {
        try {
          const results = await Promise.all([
              getCachedFeature(LICENSE_FEATURE.CustomForms),
              getCachedFeature(LICENSE_FEATURE.CustomProperties)
          ]);
          const isCustomFormsEnabled = results[0];
          const isCustomPropertiesEnabled = results[1];

          let filter = "";
          if(!isCustomFormsEnabled)
            filter = "$filter=EntityType in ('Team', 'Category')&";
          else if(!isCustomPropertiesEnabled)
            filter = "$filter=CustomPropertyId eq null&";
          
          filter += "$expand=CustomProperty&";

          const { data: { value } } = await api.getDataSources(`?${filter}$orderby=Name`);
          const rowData: RowsData[] = value
          .filter((v: IDataSource) => !v.EntityType.startsWith("CustomProperty") || (v.EntityType.startsWith("CustomProperty") && v.CustomPropertyId != null))
          .map((ds: IDataSource) => ({
            id: ds['Id'],
            [t('template.datasource-headers.name-search')]: ds.Name,
            [t('template.datasource-headers.name')]: ds.Name,
            [t('template.datasource-headers.type')]: TEMPLATE_DROPDOWN_RETURN_LABEL[ds.EntityType] ?? ds.CustomProperty?.Name,
            "": (
              <MenuButton
              trigger={
                  <Button icon={<MoreIcon />} text iconOnly title="Actions" onClick={(e) => {
                    e.stopPropagation();
                  }} />
              }
              menu={{
                items: [
                  {
                    content: "Edit",
                    icon: <FontIcon iconName={"Edit"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
                    onClick: (e) => {
                      e.stopPropagation();
                      onRowClickHandler(ds['Id']);
                    }
                  },
                  {
                    content: toTitleCase(DELETE),
                    icon: <FontIcon iconName={"Delete"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
                    onClick: (e) => {
                      e.stopPropagation();
                      setRecordToDelete({
                        Id: ds['Id'],
                        Name: ds['Name']
                      });
                      setShowDialog(true);
                    }
                  }
                ],
              }}
              />
                )
          }));
          setRows(rowData);
        } catch (err: any) {
        }
        
        setIsLoading(false);
      }, []);

    useEffect(() => {
        fetchData();
    }, [])

    const ifEmpty = {
      header: t('template.datasource.there-are-no-data'),
      subHeader: t('template.datasource.get-started-by-clicking')
    };
    
    return <ProviderConsumer
        render={globalTheme => (
          <>
          <Toolbar
            globalTheme={globalTheme}
            title={t('template.datasource.title-plural')}
            addNew={true}
            addNewContent={
              <Button
                icon={<AddIcon />}
                text
                primary
                content={`${t('common.new')} ${ t('template.datasource.title')}`}
                onClick={() => { onAddClickHandler?.(); }}
                style={{
                  color: toolbarButtonColor(true, globalTheme.siteVariables.colorScheme),
                  height: '52px'
                }}
              />
            }
          />

          {/* Main Section */}
          {(isLoading) ? <LoadingScreen message={`${LOADING.replace("...", "")} ${t('template.datasource.title-plural')}...`} /> :
          <Flex style={{ marginTop: '-1.25rem' }} className="height--150" column gap='gap.medium'>
            <Flex.Item grow styles={{ paddingTop: '1.25rem', overflow: 'auto' }}>
              <div style={{ padding: 10 }} className={`${Styles.dsTableParent}`}>
                {ifEmpty && (
                  <>
                    {rows.length == 0 && (
                      <EmptyData
                        headerText={ifEmpty.header}
                        subheaderText={ifEmpty.subHeader}
                        SVGIcon={<EmptySVG width={200} height={200} />}
                        style={{color:`${globalTheme.siteVariables.theme === themeNames.Dark ? 'white' : 'black'}`}}
                      />
                    )}
                    {rows.length != 0 && (
                    <TableWrapper
                      headers={headers}
                      onRowClickHandler={onRowClickHandler}
                      rows={rows}
                      routeName={t('template.datasource.title-plural')}
                      setCurrentPage={setCurrentPage}
                      currentPage={currentPage}
                    />
                  )}
                  <Dialog
                    open={showDialog}
                    onCancel={() => setShowDialog(false)}
                    cancelButton={toTitleCase(BUTTONS.CANCEL)}
                    confirmButton={toTitleCase(DELETE)}
                    onConfirm={() => {
                      deleteDatasource();
                    }}
                    content={transformModalContent`${t('template.datasource.delete-message', {
                      recordToDelete: recordToDelete['Name']
                    })}`}
                    header={`${t('template.datasource.header-delete-message',{
                      recordToDelete: recordToDelete['Name']
                    })}`}
                    style={{ maxWidth: '500px' }}
                  />
                  </>
                )}
              </div>
            </Flex.Item>
          </Flex>
          }
        </>)}
    />
}